import { ContentState, StatusPill } from "@whitehaven/ui";
import type {
  AdminLeadDuplicateCandidate,
  AdminLeadDuplicateCandidateSetRevision,
  AdminLeadDuplicateCursor,
  AdminLeadDuplicateSummary,
  AdminLeadGuestConversionDuplicateReview,
  LeadId,
} from "@whitehaven/contracts";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  captureProtectedStateGeneration,
  isProtectedStateGenerationCurrent,
  registerProtectedStateCleaner,
} from "../../protected-state";
import {
  adminLeadDuplicateSummariesEqual,
  adminLeadDuplicateSummaryMatchesCompleteCandidates,
  duplicateMatchKindsLabel,
  isAdminLeadDuplicatePageConsistent,
  mergeAdminLeadDuplicateCandidates,
} from "./admin-lead-duplicate-model";
import {
  ADMIN_LEAD_DUPLICATE_PAGE_SIZE,
  type AdminLeadDuplicateGateway,
} from "./lead-duplicate-gateway";
import { createAdminLeadDetailHref } from "./lead-detail-route-model";
import { leadPipelineStageLabels } from "./pipeline-stage-model";

export type AdminLeadDuplicateNotice =
  | "REFRESH_FAILED"
  | "LOAD_MORE_FAILED"
  | "REFRESH_CONFLICT"
  | "LOAD_MORE_CONFLICT"
  | null;

type AdminLeadDuplicateState =
  | { readonly kind: "unavailable" }
  | { readonly kind: "loading" }
  | { readonly kind: "error" }
  | {
      readonly kind: "ready";
      readonly candidateSetRevision: AdminLeadDuplicateCandidateSetRevision;
      readonly summary: AdminLeadDuplicateSummary;
      readonly items: readonly AdminLeadDuplicateCandidate[];
      readonly nextCursor: AdminLeadDuplicateCursor | null;
      readonly consumedCursors: readonly AdminLeadDuplicateCursor[];
      readonly refreshing: boolean;
      readonly loadingMore: boolean;
      readonly notice: AdminLeadDuplicateNotice;
    };

interface AdminLeadDuplicatePanelProps {
  readonly leadId: LeadId;
  readonly gateway: AdminLeadDuplicateGateway | null;
  readonly refreshEpoch?: number;
  readonly disabled?: boolean;
  readonly onSummaryConfirmed?: (summary: AdminLeadDuplicateSummary) => void;
  readonly onReviewStateChange?: (
    review: AdminLeadGuestConversionDuplicateReview | null,
  ) => void;
}

type DuplicateRequestMode = "initial" | "refresh" | "load-more";

const duplicateDateFormatter = new Intl.DateTimeFormat("en-US", {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: "Pacific/Honolulu",
});

function formatDuplicateDate(value: string): string {
  return duplicateDateFormatter.format(new Date(value));
}

function failedDuplicateState(
  current: AdminLeadDuplicateState,
  mode: DuplicateRequestMode,
  conflict: boolean,
): AdminLeadDuplicateState {
  if (mode === "initial" || current.kind !== "ready") {
    return { kind: "error" };
  }

  return {
    ...current,
    refreshing: false,
    loadingMore: false,
    notice:
      mode === "refresh"
        ? conflict
          ? "REFRESH_CONFLICT"
          : "REFRESH_FAILED"
        : conflict
          ? "LOAD_MORE_CONFLICT"
          : "LOAD_MORE_FAILED",
  };
}

function CandidateRecord({
  candidate,
}: {
  readonly candidate: AdminLeadDuplicateCandidate;
}) {
  const detailHref = createAdminLeadDetailHref(candidate.representativeLeadId);

  return (
    <article className="crm-lead-duplicates__candidate">
      <div className="crm-lead-engagement__record-heading">
        <h4>
          {detailHref ? (
            <a href={detailHref}>{candidate.displayName}</a>
          ) : (
            candidate.displayName
          )}
        </h4>
        <StatusPill tone="warning">Possible match</StatusPill>
      </div>
      <dl className="crm-lead-duplicates__facts">
        <div>
          <dt>Review signal</dt>
          <dd>{duplicateMatchKindsLabel(candidate.matchKinds)}</dd>
        </div>
        <div>
          <dt>Representative lead stage</dt>
          <dd>{leadPipelineStageLabels[candidate.pipelineStage]}</dd>
        </div>
        <div>
          <dt>Active lead records</dt>
          <dd>{candidate.activeLeadCount}</dd>
        </div>
        <div>
          <dt>Representative lead created</dt>
          <dd>
            <time dateTime={candidate.representativeLeadCreatedAt}>
              {formatDuplicateDate(candidate.representativeLeadCreatedAt)}
            </time>
          </dd>
        </div>
      </dl>
    </article>
  );
}

function noticeTitle(notice: Exclude<AdminLeadDuplicateNotice, null>): string {
  switch (notice) {
    case "REFRESH_FAILED":
      return "Possible matches could not be refreshed.";
    case "LOAD_MORE_FAILED":
      return "More possible matches could not be loaded.";
    case "REFRESH_CONFLICT":
    case "LOAD_MORE_CONFLICT":
      return "Possible matches changed while this view was loading.";
  }
}

export function AdminLeadDuplicatePanel({
  leadId,
  gateway,
  refreshEpoch = 0,
  disabled = false,
  onSummaryConfirmed,
  onReviewStateChange,
}: AdminLeadDuplicatePanelProps) {
  const [state, setState] = useState<AdminLeadDuplicateState>(
    gateway ? { kind: "loading" } : { kind: "unavailable" },
  );
  const stateRef = useRef(state);
  stateRef.current = state;
  const onSummaryConfirmedRef = useRef(onSummaryConfirmed);
  onSummaryConfirmedRef.current = onSummaryConfirmed;
  const onReviewStateChangeRef = useRef(onReviewStateChange);
  onReviewStateChangeRef.current = onReviewStateChange;
  const observedRefreshEpoch = useRef(refreshEpoch);
  const requestSequence = useRef(0);
  const activeRequest = useRef<AbortController | null>(null);

  const commitState = useCallback(
    (
      next:
        | AdminLeadDuplicateState
        | ((current: AdminLeadDuplicateState) => AdminLeadDuplicateState),
    ) => {
      setState((current) => {
        const resolved = typeof next === "function" ? next(current) : next;
        stateRef.current = resolved;
        return resolved;
      });
    },
    [],
  );

  const requestPage = useCallback(
    async (
      mode: DuplicateRequestMode,
      cursor: AdminLeadDuplicateCursor | null,
    ) => {
      if (!gateway) {
        commitState({ kind: "unavailable" });
        return;
      }

      const beforeRequest = stateRef.current;
      if (mode === "load-more" && beforeRequest.kind !== "ready") return;

      const requestId = requestSequence.current + 1;
      requestSequence.current = requestId;
      activeRequest.current?.abort();
      const controller = new AbortController();
      activeRequest.current = controller;
      const generation = captureProtectedStateGeneration();
      const expectedRevision =
        mode === "load-more" && beforeRequest.kind === "ready"
          ? beforeRequest.candidateSetRevision
          : null;

      onReviewStateChangeRef.current?.(null);
      commitState((current) => {
        if (mode === "initial" || current.kind !== "ready") {
          return { kind: "loading" };
        }
        return {
          ...current,
          refreshing: mode === "refresh",
          loadingMore: mode === "load-more",
          notice: null,
        };
      });

      try {
        const page = await gateway(
          { leadId, cursor, limit: ADMIN_LEAD_DUPLICATE_PAGE_SIZE },
          { signal: controller.signal },
        );
        if (
          controller.signal.aborted ||
          requestSequence.current !== requestId ||
          !isProtectedStateGenerationCurrent(generation)
        ) {
          return;
        }

        if (
          !isAdminLeadDuplicatePageConsistent(
            page,
            leadId,
            cursor,
            expectedRevision,
          )
        ) {
          commitState((current) => failedDuplicateState(current, mode, true));
          return;
        }

        const current = stateRef.current;
        if (mode === "load-more") {
          if (
            current.kind !== "ready" ||
            cursor === null ||
            current.candidateSetRevision !== page.candidateSetRevision ||
            !adminLeadDuplicateSummariesEqual(current.summary, page.summary) ||
            current.consumedCursors.includes(cursor) ||
            (page.nextCursor !== null &&
              current.consumedCursors.includes(page.nextCursor))
          ) {
            commitState((confirmed) =>
              failedDuplicateState(confirmed, mode, true),
            );
            return;
          }

          const items = mergeAdminLeadDuplicateCandidates(
            current.items,
            page.items,
          );
          if (
            items === null ||
            items.length > page.summary.candidateContactCount ||
            (page.nextCursor === null &&
              !adminLeadDuplicateSummaryMatchesCompleteCandidates(
                page.summary,
                items,
              )) ||
            (page.nextCursor !== null &&
              items.length >= page.summary.candidateContactCount)
          ) {
            commitState((confirmed) =>
              failedDuplicateState(confirmed, mode, true),
            );
            return;
          }

          onSummaryConfirmedRef.current?.(page.summary);
          if (page.nextCursor === null) {
            onReviewStateChangeRef.current?.({
              leadId,
              candidateSetRevision: page.candidateSetRevision,
              summary: page.summary,
            });
          }
          commitState({
            kind: "ready",
            candidateSetRevision: page.candidateSetRevision,
            summary: page.summary,
            items,
            nextCursor: page.nextCursor,
            consumedCursors: [...current.consumedCursors, cursor],
            refreshing: false,
            loadingMore: false,
            notice: null,
          });
          return;
        }

        onSummaryConfirmedRef.current?.(page.summary);
        if (page.nextCursor === null) {
          onReviewStateChangeRef.current?.({
            leadId,
            candidateSetRevision: page.candidateSetRevision,
            summary: page.summary,
          });
        }
        commitState({
          kind: "ready",
          candidateSetRevision: page.candidateSetRevision,
          summary: page.summary,
          items: page.items,
          nextCursor: page.nextCursor,
          consumedCursors: [],
          refreshing: false,
          loadingMore: false,
          notice: null,
        });
      } catch {
        if (
          controller.signal.aborted ||
          requestSequence.current !== requestId ||
          !isProtectedStateGenerationCurrent(generation)
        ) {
          return;
        }
        commitState((current) => failedDuplicateState(current, mode, false));
      } finally {
        if (activeRequest.current === controller) {
          activeRequest.current = null;
        }
      }
    },
    [commitState, gateway, leadId],
  );

  useEffect(() => {
    const unregisterCleaner = registerProtectedStateCleaner(
      "crm-admin-lead-duplicate-review",
      () => {
        requestSequence.current += 1;
        activeRequest.current?.abort();
        activeRequest.current = null;
        onReviewStateChangeRef.current?.(null);
        commitState(gateway ? { kind: "loading" } : { kind: "unavailable" });
      },
    );

    void requestPage("initial", null);

    return () => {
      unregisterCleaner();
      requestSequence.current += 1;
      activeRequest.current?.abort();
      activeRequest.current = null;
      onReviewStateChangeRef.current?.(null);
    };
  }, [commitState, gateway, requestPage]);

  useEffect(() => {
    if (observedRefreshEpoch.current === refreshEpoch) return;
    observedRefreshEpoch.current = refreshEpoch;
    void requestPage(
      stateRef.current.kind === "ready" ? "refresh" : "initial",
      null,
    );
  }, [refreshEpoch, requestPage]);

  const ready = state.kind === "ready" ? state : null;
  const busy =
    state.kind === "loading" ||
    (ready !== null && (ready.refreshing || ready.loadingMore));
  const conflict =
    ready?.notice === "REFRESH_CONFLICT" ||
    ready?.notice === "LOAD_MORE_CONFLICT";

  return (
    <section
      className="crm-lead-duplicates crm-lead-engagement"
      aria-labelledby="crm-lead-duplicates-title"
      aria-describedby="crm-lead-duplicates-scope"
      aria-busy={busy ? "true" : undefined}
    >
      <div className="crm-lead-engagement__heading">
        <div>
          <h3 id="crm-lead-duplicates-title">
            Possible matching contact records
          </h3>
          <p id="crm-lead-duplicates-scope">
            An exact normalized email or phone match is a review signal only. It
            does not prove that two records describe the same person, and this
            view makes no record changes.
          </p>
        </div>
        {gateway && ready && (
          <button
            type="button"
            className="crm-lead-detail__button"
            disabled={busy || disabled}
            onClick={() => void requestPage("refresh", null)}
          >
            {ready.refreshing ? "Refreshing matches" : "Refresh matches"}
          </button>
        )}
      </div>

      {state.kind === "unavailable" && (
        <ContentState
          kind="warning"
          headingLevel={4}
          title="Duplicate review is unavailable."
        >
          <p>
            The protected duplicate-review service has not been connected. No
            sample or cached matching records are shown.
          </p>
        </ContentState>
      )}

      {state.kind === "loading" && (
        <ContentState
          kind="loading"
          headingLevel={4}
          title="Loading possible matching contact records."
        >
          <p>No unconfirmed matching records are shown.</p>
        </ContentState>
      )}

      {state.kind === "error" && (
        <ContentState
          kind="error"
          headingLevel={4}
          title="Possible matching records could not be loaded."
          actions={
            <button
              type="button"
              className="crm-lead-detail__button"
              disabled={disabled}
              onClick={() => void requestPage("initial", null)}
            >
              Try duplicate review again
            </button>
          }
        >
          <p>No unconfirmed matching records are shown.</p>
        </ContentState>
      )}

      {ready?.notice && (
        <ContentState
          kind="error"
          headingLevel={4}
          title={noticeTitle(ready.notice)}
          actions={
            <button
              type="button"
              className="crm-lead-detail__button"
              disabled={busy || disabled}
              onClick={() =>
                void requestPage(
                  conflict || ready.notice === "REFRESH_FAILED"
                    ? "refresh"
                    : "load-more",
                  conflict || ready.notice === "REFRESH_FAILED"
                    ? null
                    : ready.nextCursor,
                )
              }
            >
              {conflict || ready.notice === "REFRESH_FAILED"
                ? "Refresh matching records"
                : "Try loading more again"}
            </button>
          }
        >
          <p>
            The last confirmed possible matches remain visible. No record
            changes were made.
          </p>
        </ContentState>
      )}

      {ready?.summary.eligibility === "TARGET_INACTIVE" && (
        <ContentState
          kind="warning"
          headingLevel={4}
          title="Duplicate review is unavailable for this inactive lead."
        >
          <p>
            No matching candidates are shown because this review is limited to
            active target leads and active candidate records.
          </p>
        </ContentState>
      )}

      {ready?.summary.eligibility === "ELIGIBLE" &&
        ready.summary.candidateContactCount === 0 && (
          <ContentState
            kind="empty"
            headingLevel={4}
            title="No possible matching contact records were found."
          >
            <p>
              The current organization-scoped normalized email and phone check
              returned no active candidates. This is not proof of identity or
              uniqueness outside this review.
            </p>
          </ContentState>
        )}

      {ready?.summary.eligibility === "ELIGIBLE" &&
        ready.summary.candidateContactCount > 0 && (
          <>
            <p
              className="crm-lead-detail__refresh-status"
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              {ready.loadingMore
                ? "Loading more possible matching records."
                : ready.refreshing
                  ? "Refreshing possible matching records. The last confirmed review remains visible."
                  : `${ready.items.length} of ${ready.summary.candidateContactCount} possible matching contact ${
                      ready.summary.candidateContactCount === 1
                        ? "record"
                        : "records"
                    } loaded.`}
            </p>
            <ol
              className="crm-lead-engagement__record-list"
              aria-label="Possible matching contact records"
            >
              {ready.items.map((candidate) => (
                <li key={candidate.representativeLeadId}>
                  <CandidateRecord candidate={candidate} />
                </li>
              ))}
            </ol>
            {ready.nextCursor && !conflict && (
              <button
                type="button"
                className="crm-lead-detail__button crm-lead-detail__button--secondary"
                disabled={busy || disabled}
                onClick={() => void requestPage("load-more", ready.nextCursor)}
              >
                {ready.loadingMore
                  ? "Loading more matches"
                  : "Load more possible matches"}
              </button>
            )}
          </>
        )}
    </section>
  );
}
